import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { store, useAppSelector } from '../../../store';
import { DetailHeader } from '../../common/DetailHeader';
import { QcsBox } from '../../common/basic/QcsBox';
import { Modal, Tab, Tooltip } from '@mui/material';
import { QcsChip } from '../../common/basic/QcsChip';
import { TabContext, TabPanel } from '@mui/lab';
import { QcsTabs } from '../../common/basic/QcsTabs';
import { InvTabSupplier } from './Tabs/InvTabSupplier';
import { InvTabWorkplaces } from './Tabs/InvTabWorkplaces';
import { QcsTypography } from '../../common/basic/QcsTypography';
import { selectIdentity, selectIdentityRowsCount } from '../../../store/entities/identity';
import { hasRole, isExternal } from '../../../utils/roles';
import { InvTabActivities } from './Tabs/InvTabActivities';
import { InvTabWorkers } from './Tabs/InvTabWorkers';
import { InvTabAttachments } from './Tabs/InvTabAttachments';
import { getLangNameObj, invitationCertificateIsApproved } from '../../../utils/format';
import { invitationListActions } from '../../../store/entities/invitationList';
import { InvTabLogs } from './Tabs/InvTabLogs';
import { InvitationEditTabLabel, InvitationIconType } from './InvitationEditTabLabel';
import { SafetyEquipmentTable } from './Tabs/SafetyEquipmentTable';
import { QcsButton } from '../../common/basic/QcsButton';
import { invitationGetUpdateDto, invitationLock, useInvitation } from './Tabs/invitationFunctions';
import { InvTabDetail } from './Tabs/InvTabDetail';
import { FaIcon } from '../../common/FaIcon';
import { InlineEditableTitle } from './InlineEditableTitle';
import { INVITATION_AUDIT_TIMEOUT_MS } from '../../../utils/constants';
import { invitationStateAudActions, selectInvitationStateAud } from '../../../store/entities/invitationStateAud';
import { invitationActions } from '../../../store/entities/invitation';
import { invitationHistoryListActions } from '../../../store/entities/invitationHistoryList';
import { invitationPersonListActions } from '../../../store/entities/invitationPersonList';
import axios, { CancelTokenSource } from 'axios';
import { PathKey, useNavigation } from '../../../utils/urlPaths';
import HandoverProtocol from './Tabs/HandoverProtocol';
import { QcsModalBox } from '../../common/basic/QcsModalBox';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { InvTabEntryRequests } from './Tabs/InvTabEntryRequests';
import { faFile, faPersonWalking, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import { InvitationType } from '@api/models/enums/InvitationType';
import { InvitationState } from '@api/models/enums/InvitationState';
import { UserRole } from '@api/models/enums/UserRole';
import { invitationPersonService, invitationService } from '@api/services/services';
import { ConfirmResponsiblePersonModal } from './ConfirmResponsiblePersonModal';

export const InvitationEdit: FC = () => {
  const { t, i18n, invitation, dispatch, callWithLock, reloadInvitation } = useInvitation();

  const identity = useAppSelector(selectIdentity);
  const { getBackPath: backTo, navigate, currentPath } = useNavigation('/invitation');
  const { invitationId, tabId = 'detail' } = useParams();
  const [handoverDirty, setHandoverDirty] = useState(false);
  const [showHandoverDirtyModal, setShowHandoverDirtyModal] = useState(false);
  const [tabNewValue, setTabNewValue] = useState(tabId);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelToken = useRef<CancelTokenSource | null>(null);
  const businessSettings = useBusinessSettings();
  const [responsiblePersonModalOpen, setResponsiblePersonModalOpen] = useState(false);

  const handleTimer = async () => {
    try {
      await handleTimer2();
    } catch (err) {
      //Ignore.
    }

    if (cancelToken.current!.token.reason) {
      return;
    }

    timeoutRef.current = setTimeout(handleTimer, INVITATION_AUDIT_TIMEOUT_MS);
  };

  useEffect(() => {
    cancelToken.current = axios.CancelToken.source();
    timeoutRef.current = setTimeout(handleTimer, INVITATION_AUDIT_TIMEOUT_MS);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      cancelToken.current!.cancel();
      clearTimeout(timeoutRef.current!);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimer2 = async () => {
    if (invitationLock.locked) {
      return;
    }

    const auditResponse = await invitationService.getInvitationAudit(
      invitationId!,
      { page: 0, size: 1 },
      { cancelToken: cancelToken.current!.token },
    );

    if (invitationLock.locked) {
      return;
    }

    const data = auditResponse.data.content?.[0];
    if (!data) {
      return;
    }

    const oldData = selectInvitationStateAud(store.getState());
    if (oldData?.timestamp === data.timestamp) {
      return;
    }

    cancelToken.current!.token.throwIfRequested();
    dispatch(invitationStateAudActions.success(data));

    const [dataResponse, personsResponse] = await Promise.all([
      invitationService.getById(invitationId!, {}, { cancelToken: cancelToken.current!.token }),
      invitationPersonService.query({ invitationId, size: selectIdentityRowsCount(store.getState()) }, { cancelToken: cancelToken.current!.token }),
    ]);

    cancelToken.current!.token.throwIfRequested();
    dataResponse.data.persons = personsResponse.data.content;
    dispatch(invitationActions.success(dataResponse.data));
    dispatch(invitationPersonListActions.success(personsResponse.data));
    dispatch(invitationListActions.reload(true));
    dispatch(invitationHistoryListActions.reload(true));
  };

  const handleActiveTab = (event: React.SyntheticEvent, newValue: string) => {
    if (handoverDirty) {
      setShowHandoverDirtyModal(true);
      setTabNewValue(newValue);
      setTimeout(() => {
        setHandoverDirty(false);
      }, 1000);
      return;
    }
    navigate(`/invitation/${invitationId}/${newValue}`);
  };

  const isOneTime =
    invitation.type === InvitationType.OneTime ||
    invitation.type === InvitationType.OneTimeEntry ||
    hasRole(identity.roles, [UserRole.ExternalWorker]);

  const isExternalManager = hasRole(identity.roles, [UserRole.ExternalManager]);

  useEffect(() => {
    if (isExternalManager && (invitation.state === InvitationState.Sent || invitation.state === InvitationState.Viewed)) {
      setResponsiblePersonModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabRights = hasRole(identity.roles, [
    UserRole.ExternalManager,
    UserRole.ExternalOneTime,
    UserRole.AdminQcs,
    UserRole.AdminCompany,
    UserRole.ManagerOsah,
    UserRole.ManagerWorkplace,
    UserRole.Receptionist,
    UserRole.ExternalWorker,
  ]);
  const enableTabs =
    tabRights &&
    //One time has no "checked" state.
    (isOneTime ||
      !isExternalManager ||
      //Supplier details already checked - NOT IN.
      (invitation.state !== InvitationState.Sent && invitation.state !== InvitationState.Viewed));

  const allPersonAllDocumentUploaded = useMemo(
    () =>
      (invitation.persons ?? []).every((person) =>
        (person.documents ?? []).every((document) =>
          isExternal(identity) ? !!document.uploadedDocument : !!document.uploadedDocument && invitationCertificateIsApproved(document),
        ),
      ),
    [identity, invitation.persons],
  );

  const areAllWorkplaceRisksApproved = useMemo(
    () => (invitation.workplaces ?? []).every((workplace) => (workplace.risks ?? []).every((risk) => !!risk.approvedOn)),
    [invitation.workplaces],
  );

  const areAllRequiredActivitiesApproved = useMemo(
    () => (invitation.activities ?? []).every((activity) => !activity.activity?.riskRequired || !!activity.approvedOn),
    [invitation.activities],
  );

  const areAllRequiredDocumentsUploaded = useMemo(
    () => (invitation.activities ?? []).every((activity) => !activity.activity?.riskRequired || !!activity.document),
    [invitation.activities],
  );

  const totalPersonCount = invitation.overview?.requiredTotalCount ?? 0;
  const trainedPersonCount = totalPersonCount - (invitation.overview?.requiredUntrainedCount ?? 0);

  const invitationStateColor: Record<string, { bgcolor: string; color: string }> = {
    [InvitationState.Sent]: { bgcolor: 'info.main', color: 'info.contrastText' },
    [InvitationState.Viewed]: { bgcolor: 'info.main', color: 'info.contrastText' },
    [InvitationState.PartiallyFilled]: { bgcolor: 'warning.main', color: 'warning.contrastText' },
    [InvitationState.WaitingForApproval]: { bgcolor: 'warning.main', color: 'warning.contrastText' },
    [InvitationState.Approved]: { bgcolor: 'success.main', color: 'success.contrastText' },
    [InvitationState.Archived]: { bgcolor: 'grey.800', color: 'common.white' },
  };

  const trainingChipColor = (() => {
    if (totalPersonCount === 0) return undefined;
    if (trainedPersonCount === totalPersonCount) return { bgcolor: 'success.main', color: 'success.contrastText' };
    if (trainedPersonCount === 0) return { bgcolor: 'error.main', color: 'error.contrastText' };
    return { bgcolor: 'warning.main', color: 'warning.contrastText' };
  })();

  return (
    <>
      {responsiblePersonModalOpen && (
        <ConfirmResponsiblePersonModal open={responsiblePersonModalOpen} onClose={() => setResponsiblePersonModalOpen(false)} />
      )}
      <Modal open={showHandoverDirtyModal} onClose={() => setShowHandoverDirtyModal(false)}>
        <QcsModalBox>
          <QcsTypography variant="h6">{t('invitation.dirtyHandoverModalTitle')}</QcsTypography>
          <QcsTypography variant="subtitle1">{t('invitation.dirtyHandoverModalMessage')}</QcsTypography>
          <QcsBox sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
            <QcsButton onClick={() => setShowHandoverDirtyModal(false)} variant="outlined">
              {t('common.abort')}
            </QcsButton>
            <QcsButton
              onClick={() => {
                setShowHandoverDirtyModal(false);
                navigate(`/invitation/${invitationId}/${tabNewValue}`);
              }}
              variant="contained"
            >
              {t('training.unsavedChangesDialog.continueWithoutSaving')}
            </QcsButton>
          </QcsBox>
        </QcsModalBox>
      </Modal>

      <DetailHeader
        backTo={backTo}
        titleContent={
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {(invitation.type === InvitationType.OneTime || invitation.type === InvitationType.OneTimeEntry) && (
              <Tooltip title={t('invitation.visitInvitation')}>
                <span>
                  <FaIcon icon={faPersonWalking} style={{ marginRight: '0.5rem' }} />
                </span>
              </Tooltip>
            )}
            <InlineEditableTitle
              value={getLangNameObj(i18n, invitation)}
              disabled={invitation.state === InvitationState.Archived || !hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace])}
              onSave={async (newName) => {
                await callWithLock(async () => {
                  const data = invitationGetUpdateDto(invitation);
                  data.name = newName;
                  await invitationService.update(invitation.id!, data);
                  await reloadInvitation();
                });
              }}
            />
          </div>
        }
      />

      <QcsBox
        sx={{
          pb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        {invitation.supplier?.name && (
          <QcsChip
            isBold
            label={invitation.supplier.name}
            variant="outlined"
            onClick={
              invitation.supplier.id && !isOneTime
                ? () => navigate(PathKey.SupplierDetail, { supplierId: invitation.supplier!.id! }, { backTo: currentPath })
                : undefined
            }
            sx={invitation.supplier.id && !isOneTime ? { cursor: 'pointer' } : undefined}
          />
        )}
        <QcsChip
          isBold
          label={t(`invitation.states.${invitation.state}`)}
          sx={invitationStateColor[invitation.state!] ?? { bgcolor: 'primary.main', color: 'primary.contrastText' }}
        />
        <QcsChip isBold label={`${trainedPersonCount} / ${totalPersonCount} ${t('invitation.trained')}`} sx={trainingChipColor} />
      </QcsBox>
      <QcsBox>
        <TabContext value={tabId}>
          <QcsTabs onChange={handleActiveTab}>
            <Tab key="detail" label={<InvitationEditTabLabel value="detail" type={InvitationIconType.Info} />} value="detail" />
            {enableTabs && (
              <Tab
                key="tab2"
                label={
                  <InvitationEditTabLabel
                    value={isOneTime ? 'visit' : 'person'}
                    type={allPersonAllDocumentUploaded ? InvitationIconType.Ok : InvitationIconType.Warning}
                  />
                }
                value="person"
              />
            )}
            {enableTabs && !isOneTime && (
              <Tab
                key="tab3"
                label={
                  <InvitationEditTabLabel
                    value="workplace"
                    type={areAllWorkplaceRisksApproved ? InvitationIconType.Ok : InvitationIconType.Warning}
                  />
                }
                value="workplace"
              />
            )}
            {enableTabs && !isOneTime && (
              <Tab
                key="tab4"
                label={
                  <InvitationEditTabLabel
                    value="activity"
                    type={
                      (isExternal(identity) || areAllRequiredActivitiesApproved) && areAllRequiredDocumentsUploaded
                        ? InvitationIconType.Ok
                        : InvitationIconType.Warning
                    }
                  />
                }
                value="activity"
              />
            )}
            {enableTabs && invitation.entryRequestRequired && businessSettings.allowEntryRequests && (
              <Tab
                key="tab9"
                label={<InvitationEditTabLabel value="entryRequest" type={InvitationIconType.Other} icon={faSignInAlt} />}
                value="entryRequest"
              />
            )}
            {enableTabs && invitation.createHandoverProtocol && businessSettings.workplaceHandoverEnabled && (
              <Tab
                key="tab7"
                label={<InvitationEditTabLabel value="handoverProtocol" type={InvitationIconType.Other} icon={faFile} />}
                value="handoverProtocol"
              />
            )}
            <Tab
              key="tab5"
              label={
                <InvitationEditTabLabel
                  value="attachment"
                  type={(invitation.attachments?.length ?? 0) > 0 ? InvitationIconType.Info : InvitationIconType.InfoInactive}
                />
              }
              value="attachment"
            />
            {enableTabs && (
              <Tab key="tab6" label={<InvitationEditTabLabel value="safetyEquipment" type={InvitationIconType.Info} />} value="safetyEquipment" />
            )}
            {!isOneTime && <Tab key="tab1" label={<InvitationEditTabLabel value="supplier" type={InvitationIconType.Info} />} value="supplier" />}
            <Tab key="tab8" label={<InvitationEditTabLabel value="log" type={InvitationIconType.Info} />} value="log" />
          </QcsTabs>
          <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="detail">
            <InvTabDetail />
          </TabPanel>
          {!isOneTime && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="supplier">
              <InvTabSupplier />
            </TabPanel>
          )}
          {enableTabs && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="person">
              <InvTabWorkers />
            </TabPanel>
          )}
          {enableTabs && !isOneTime && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="workplace">
              <InvTabWorkplaces />
            </TabPanel>
          )}
          {enableTabs && !isOneTime && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="activity">
              <InvTabActivities />
            </TabPanel>
          )}
          {enableTabs && businessSettings?.allowEntryRequests && invitation.entryRequestRequired && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="entryRequest">
              <InvTabEntryRequests />
            </TabPanel>
          )}
          {enableTabs && businessSettings?.workplaceHandoverEnabled && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="handoverProtocol">
              <HandoverProtocol onDirtyChange={setHandoverDirty} />
            </TabPanel>
          )}
          <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="attachment">
            <InvTabAttachments />
          </TabPanel>
          {enableTabs && (
            <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="safetyEquipment">
              <SafetyEquipmentTable />
            </TabPanel>
          )}
          <TabPanel sx={{ paddingX: { xs: 0, sm: 2 } }} value="log">
            <InvTabLogs />
          </TabPanel>
        </TabContext>
      </QcsBox>
    </>
  );
};
